import { z } from 'zod';

/**
 * Two-factor authentication. Mandatory TOTP for admins (ADR-014); optional
 * for clients and therapists, who choose between an authenticator app and a
 * code emailed to them (ADR-017).
 *
 * The choice is the point. A therapist on a work laptop wants TOTP; a client
 * who signed up on a borrowed phone often has nothing but their mailbox.
 * Offering only the stronger factor means the second group turns 2FA off
 * entirely, which protects them less, not more.
 *
 * See auth.ts for the login-flow contracts (LoginResponseSchema's
 * `twoFactorRequired` branch, VerifyTwoFactorRequestSchema).
 */

export const TwoFactorMethodSchema = z.enum(['TOTP', 'EMAIL']);
export type TwoFactorMethod = z.infer<typeof TwoFactorMethodSchema>;

export const TwoFactorStatusResponseSchema = z.object({
  enabled: z.boolean(),
  /** Null when the user has never started enrolment. */
  method: TwoFactorMethodSchema.nullable(),
  confirmedAt: z.string().datetime().nullable(),
});
export type TwoFactorStatusResponse = z.infer<typeof TwoFactorStatusResponseSchema>;

/**
 * The secret is shown once, in plain text, alongside the QR code — for
 * manual entry when scanning isn't possible. Nothing here is a bearer
 * credential by itself (2FA isn't enabled until POST /confirm succeeds).
 */
export const TwoFactorSetupRequestSchema = z.object({
  method: TwoFactorMethodSchema.default('TOTP'),
});
export type TwoFactorSetupRequest = z.infer<typeof TwoFactorSetupRequestSchema>;

/**
 * TOTP returns the secret and QR code; EMAIL returns none of them — there is
 * nothing to scan, the code has already been sent. `method` tells the client
 * which of the two it is looking at, so it never renders an empty QR box.
 */
export const TwoFactorSetupResponseSchema = z.object({
  method: TwoFactorMethodSchema,
  secret: z.string().nullable(),
  otpauthUrl: z.string().nullable(),
  /** data:image/png;base64,... — rendered directly in an <img> tag. TOTP only. */
  qrCodeDataUrl: z.string().nullable(),
  /** EMAIL only: the address the code went to, masked for display. */
  sentTo: z.string().nullable(),
});
export type TwoFactorSetupResponse = z.infer<typeof TwoFactorSetupResponseSchema>;

export const TwoFactorConfirmRequestSchema = z.object({
  code: z.string().length(6),
});
export type TwoFactorConfirmRequest = z.infer<typeof TwoFactorConfirmRequestSchema>;

/** Recovery codes are returned ONLY here, ONLY once — never retrievable again after this response. */
export const TwoFactorConfirmResponseSchema = z.object({
  recoveryCodes: z.array(z.string()),
});
export type TwoFactorConfirmResponse = z.infer<typeof TwoFactorConfirmResponseSchema>;

/**
 * EMAIL method only: re-send the login code. Rate limited server-side — a
 * resend button that quietly does nothing is worse than one that says why.
 */
export const TwoFactorResendResponseSchema = z.object({
  sentTo: z.string(),
});
export type TwoFactorResendResponse = z.infer<typeof TwoFactorResendResponseSchema>;

/** Disabling requires both the account password AND a valid second factor (current TOTP code or an unused recovery code). */
export const TwoFactorDisableRequestSchema = z.object({
  password: z.string().min(1),
  code: z.string().min(6).max(11),
});
export type TwoFactorDisableRequest = z.infer<typeof TwoFactorDisableRequestSchema>;
